import { useCallback, useEffect, useRef } from "react";
import { useSignalRContext } from "@/lib/signalR";

export function useSignalR() {
  return useSignalRContext();
}

export function useSignalRConnection(url?: string, autoConnect = false) {
  const { connect, disconnect, connectionState, isConnected, error } =
    useSignalRContext();
  const hasConnected = useRef(false);

  useEffect(() => {
    if (
      autoConnect &&
      url &&
      !hasConnected.current &&
      connectionState === "Disconnected"
    ) {
      hasConnected.current = true;
      connect(url);
    }
  }, [autoConnect, url, connect, connectionState]);

  const connectToHub = useCallback(
    (hubUrl: string) => {
      return connect(hubUrl);
    },
    [connect]
  );

  const disconnectFromHub = useCallback(() => {
    hasConnected.current = false;
    return disconnect();
  }, [disconnect]);

  return {
    connect: connectToHub,
    disconnect: disconnectFromHub,
    connectionState,
    isConnected,
    error,
  };
}

export function useSignalRMessage<T = unknown>(methodName: string) {
  const { onMessage, offMessage, lastMessage } = useSignalRContext();
  const callbackRef = useRef<((...args: unknown[]) => void) | null>(null);

  const subscribe = useCallback(
    (callback: (...args: unknown[]) => void) => {
      callbackRef.current = callback;
      onMessage(methodName, callback);
    },
    [methodName, onMessage]
  );

  const unsubscribe = useCallback(() => {
    if (callbackRef.current) {
      offMessage(methodName, callbackRef.current);
      callbackRef.current = null;
    }
  }, [methodName, offMessage]);

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  return {
    subscribe,
    unsubscribe,
    lastMessage: lastMessage as T,
  };
}

export function useSignalRSender() {
  const { sendMessage, isConnected } = useSignalRContext();

  const send = useCallback(
    async (methodName: string, ...args: unknown[]) => {
      if (!isConnected) {
        throw new Error("SignalR connection is not established");
      }
      return sendMessage(methodName, ...args);
    },
    [sendMessage, isConnected]
  );

  return {
    send,
    isConnected,
  };
}

export function useSignalRStatus() {
  const {
    connectionState,
    isConnected,
    error,
    reconnectAttempts,
    clearError,
    reconnect,
  } = useSignalRContext();

  return {
    connectionState: connectionState || ("Disconnected" as const),
    isConnected,
    error,
    reconnectAttempts: reconnectAttempts || 0,
    clearError: clearError || (() => {}),
    reconnect: reconnect || (async () => {}),
  };
}
